import type { AssistSuggestion } from './report-assists.js'
import type { ReportAnswer, ReportFieldType } from './report-checks.js'

/**
 * A spoken account of a job, sorted onto field keys.
 *
 * This is the matcher, and it is deliberately arithmetic rather than a model.
 * Two reasons, in order of weight. A transcript of a pest inspection carries the
 * client's name, their address and what is wrong with their house, and sending
 * that to a third party is a decision for the business and not for this file —
 * so the shipped path keeps every word on the device. And a rule that can be
 * read is a rule that can be argued with: when the sheet says it heard "tiled
 * roof" and offers Tiled, a technician can see why, which is not true of a
 * probability.
 *
 * What that costs is honesty about the ceiling. It matches what the form already
 * knows how to say — the options on a chip list, a yes or a no, a number, and a
 * clause that names its own field — and it says nothing about the rest. A
 * transcript that wanders is matched in part and reported as matched in part;
 * see `unmatched`.
 *
 * Everything here still goes through the review sheet with everything else, so
 * being wrong costs one tap. That is decision 55, and it is what makes a matcher
 * this simple safe to ship: it is allowed to be wrong, it is not allowed to be
 * quiet about it.
 *
 * Deliberately not doing dates. "Last Tuesday" on a document somebody signs is
 * the one place a cheap guess is worse than a blank.
 */

export type DictationField = {
  key: string
  label: string
  type: ReportFieldType
  options?: string[] | null
  /** The example line the sheet shows, from `FormField.dictationHint`. */
  dictationHint?: string | null
}

export type DictationMatch = AssistSuggestion & {
  source: 'heard'
  /** The words this came out of, for the sheet to quote back. */
  heard: string
}

export type DictationResult = {
  matches: DictationMatch[]
  /** Fields that were offered to the matcher and got nothing. */
  unmatched: DictationField[]
}

/** One word of the transcript: what was said, and what it compares as. */
type Word = { raw: string; norm: string }

type Clause = { text: string; words: Word[] }

const FILLER = new Set([
  'the',
  'a',
  'an',
  'is',
  'was',
  'were',
  'are',
  'it',
  'its',
  'and',
  'of',
  'for',
  'to',
  'in',
  'on',
  'at',
  'this',
  'that',
  'there',
  'here',
  'we',
  'i',
  'ive',
  'got',
  'has',
  'have',
  'had',
  'be',
  'been',
  'as',
  'with',
  'all',
  'any',
  'some',
  'so',
  'then',
  'just',
  'about',
  'over',
])

/**
 * No wins over yes.
 *
 * "No nest found" contains "found", and reading the positive marker there gets
 * the answer exactly backwards on the field a court would care about most.
 */
const NEGATIVE = new Set([
  'no',
  'not',
  'nil',
  'none',
  'nothing',
  'negative',
  'nope',
  'without',
  'absent',
  'clear',
  'cant',
  'cannot',
  'couldnt',
  'didnt',
  'wasnt',
  'werent',
  'isnt',
  'unable',
  'inaccessible',
  'declined',
])

const POSITIVE = new Set([
  'yes',
  'yep',
  'yeah',
  'found',
  'present',
  'positive',
  'active',
  'live',
  'evidence',
  'confirmed',
  'did',
  'done',
  'complete',
  'completed',
  'accessible',
])

const NUMBER_WORDS: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  fifteen: 15,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  hundred: 100,
}

/**
 * A word as it compares.
 *
 * Apostrophes close up rather than splitting, so "didn't" stays one word and
 * lands in `NEGATIVE`; everything else that is not a letter or a digit becomes a
 * boundary.
 */
function normalise(word: string): string {
  return word
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9.]+/g, ' ')
    .replace(/\.(?!\d)/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
}

function words(text: string): Word[] {
  return text
    .split(/\s+/)
    .map((raw) => ({ raw: raw.replace(/^[^\w'’]+|[^\w'’%]+$/g, ''), norm: normalise(raw) }))
    .filter((word) => word.norm.length > 0)
}

/**
 * The transcript in clauses.
 *
 * A clause is the unit a spoken answer arrives in — "single storey slab, tiled
 * roof, shed out the back" is three answers and one sentence. Matching a whole
 * transcript at once would put the roof on the storeys.
 */
function clauses(transcript: string): Clause[] {
  // A full stop between two digits is a decimal point, not the end of a
  // sentence: splitting there turns 7.5 litres into 7 litres.
  return transcript
    .split(/[,;:!?\n]+|(?<!\d)\.(?!\d)|\band then\b|\bnext\b/i)
    .map((text) => text.trim())
    .filter((text) => text.length > 0)
    .map((text) => ({ text, words: words(text) }))
}

/** Singular and plural are the same word for this purpose. */
function alike(a: string, b: string): boolean {
  if (a === b) return true
  if (a === `${b}s` || b === `${a}s`) return true
  if (a === `${b}es` || b === `${a}es`) return true
  return false
}

/** Where a phrase runs consecutively through a clause, or -1. */
function runsAt(haystack: Word[], needle: string[]): number {
  if (needle.length === 0) return -1

  for (let start = 0; start + needle.length <= haystack.length; start += 1) {
    let hit = true
    for (let offset = 0; offset < needle.length; offset += 1) {
      if (!alike(haystack[start + offset]?.norm ?? '', needle[offset] ?? '')) {
        hit = false
        break
      }
    }
    if (hit) return start
  }

  return -1
}

/**
 * A phrase found across the gaps between words, or -1.
 *
 * Speech does not agree with a form about where the spaces go: a technician says
 * "sub floor" and the option reads "Subfloor", or the reverse. Closing the gaps
 * up matches both. What that would also match, unguarded, is "shed" inside
 * "washed" — so a hit has to begin where a word begins and end where one ends,
 * give or take a plural.
 */
function collapsedRun(items: Word[], needle: string): number {
  let text = ''
  const starts = new Set<number>()
  const ends = new Set<number>()

  for (const word of items) {
    const letters = word.norm.replace(/\s+/g, '')
    if (letters.length === 0) continue
    starts.add(text.length)
    text += letters
    ends.add(text.length)
  }

  for (let at = text.indexOf(needle); at >= 0; at = text.indexOf(needle, at + 1)) {
    if (!starts.has(at)) continue
    const finish = at + needle.length
    if (ends.has(finish)) return at
    if (text.startsWith('s', finish) && ends.has(finish + 1)) return at
    if (text.startsWith('es', finish) && ends.has(finish + 2)) return at
  }

  return -1
}

/** The words of a label that identify it. "Extent of damage" → extent, damage. */
function keywords(label: string): string[] {
  return normalise(label)
    .split(' ')
    .filter((word) => word.length > 2 && !FILLER.has(word))
}

/** Whether a clause is talking about this field at all. */
function mentions(clause: Clause, field: DictationField): boolean {
  const terms = keywords(field.label)
  if (terms.length === 0) return false
  return terms.some((term) => clause.words.some((word) => alike(word.norm, term)))
}

type OptionHit = { option: string; length: number; at: number; clause: Clause }

/** Where an option was said, word for word first and then across the gaps. */
function findOption(option: string, pool: Clause[]): OptionHit | null {
  const needle = normalise(option)
    .split(' ')
    .filter((word) => word.length > 0)
  if (needle.length === 0) return null

  for (const clause of pool) {
    const at = runsAt(clause.words, needle)
    if (at >= 0) return { option, length: needle.length, at, clause }
  }

  // Short words are not worth chasing across a gap. "Nil" and "Low" turn up
  // inside too much ordinary speech to be evidence of anything.
  const joined = needle.join('')
  if (joined.length < 4) return null

  for (const clause of pool) {
    const at = collapsedRun(clause.words, joined)
    if (at >= 0) return { option, length: needle.length, at, clause }
  }

  return null
}

/** A clause, shortened to something a sheet row can hold. */
function quote(text: string, limit = 72): string {
  const clean = text.trim().replace(/\s+/g, ' ')
  return clean.length <= limit ? clean : `${clean.slice(0, limit - 1).trimEnd()}…`
}

function heardReason(text: string): string {
  return `Heard “${quote(text)}”.`
}

/** A clause with its own field name and the filler in front of it taken off. */
function statement(clause: Clause, field: DictationField): string | null {
  const terms = keywords(field.label)
  let start = 0

  while (start < clause.words.length) {
    const word = clause.words[start]?.norm ?? ''
    if (terms.some((term) => alike(word, term)) || FILLER.has(word)) start += 1
    else break
  }

  const kept = clause.words.slice(start)
  if (kept.length === 0) return null

  const said = kept.map((word) => word.raw).join(' ').trim()
  if (said.length === 0) return null

  return said.charAt(0).toUpperCase() + said.slice(1)
}

function numberIn(clause: Clause): number | null {
  for (const word of clause.words) {
    if (/^\d+(\.\d+)?$/.test(word.norm)) return Number(word.norm)
    const spoken = NUMBER_WORDS[word.norm]
    if (spoken !== undefined) return spoken
  }
  return null
}

function booleanIn(clause: Clause): boolean | null {
  const said = clause.words.map((word) => word.norm)
  if (said.some((word) => NEGATIVE.has(word))) return false
  if (said.some((word) => POSITIVE.has(word))) return true
  return null
}

/** The options a field offers, with the two-way fields given their words. */
function optionsOf(field: DictationField): string[] {
  if (field.options && field.options.length > 0) return field.options
  if (field.type === 'PASS_FAIL') return ['Pass', 'Fail']
  return []
}

function matchOne(field: DictationField, pool: Clause[]): DictationMatch | null {
  const offer = (value: ReportAnswer, heard: string): DictationMatch => ({
    fieldKey: field.key,
    value,
    source: 'heard',
    reason: heardReason(heard),
    heard: quote(heard),
  })

  if (field.type === 'SELECT' || field.type === 'MULTI_SELECT' || field.type === 'PASS_FAIL') {
    const hits = optionsOf(field)
      .map((option) => findOption(option, pool))
      .filter((hit): hit is OptionHit => hit !== null)

    if (hits.length === 0) return null

    if (field.type === 'MULTI_SELECT') {
      return offer(
        hits.map((hit) => hit.option),
        hits.map((hit) => hit.clause.text).join(', '),
      )
    }

    // The longest phrase wins. "Single storey" and "Storey" can both be options,
    // and the shorter one is inside the longer, so first-match would pick the
    // vaguer answer out of a sentence that gave the precise one.
    const best = hits.reduce((winner, hit) => (hit.length > winner.length ? hit : winner))
    return offer(best.option, best.clause.text)
  }

  const named = pool.filter((clause) => mentions(clause, field))
  if (named.length === 0) return null
  const clause = named[0] as Clause

  if (field.type === 'BOOLEAN') {
    const said = booleanIn(clause)
    return said === null ? null : offer(said, clause.text)
  }

  if (field.type === 'NUMBER' || field.type === 'DECIMAL' || field.type === 'RATING') {
    const said = numberIn(clause)
    if (said === null) return null

    // Rounded onto a whole-number question, as the cloud path's `coerce` does.
    // The two engines answer the same words and a technician cannot tell which
    // one ran, so "7.5" reaching a NUMBER field as 7.5 from the phone and as 8
    // from the model is a difference nobody could explain or reproduce.
    const whole = field.type !== 'DECIMAL'
    return offer(whole ? Math.round(said) : said, clause.text)
  }

  if (field.type === 'TEXT' || field.type === 'TEXTAREA') {
    const said = statement(clause, field)
    return said === null ? null : offer(said, clause.text)
  }

  return null
}

/**
 * A transcript against a set of fields.
 *
 * Order follows the fields as given, which is the order they are on screen: a
 * review sheet that lists answers in the order they happened to be spoken makes
 * the technician find each one twice.
 */
export function matchTranscript(
  transcript: string,
  fields: DictationField[],
): DictationResult {
  const pool = clauses(transcript)
  if (pool.length === 0) return { matches: [], unmatched: fields }

  const matches: DictationMatch[] = []
  const unmatched: DictationField[] = []

  for (const field of fields) {
    const match = matchOne(field, pool)
    if (match) matches.push(match)
    else unmatched.push(field)
  }

  return { matches, unmatched }
}
